import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Document } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminDocumentListGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<Document>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminDocumentListGetParams {
  limit: number;
  offset: number;
  sort: string;
  search: string;
  projectId: number;
  contractorId: number;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  search: 'string',
  projectId: 'number',
  contractorId: 'number',
}

export const useDocumentListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminDocumentListGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<Document>>(
    [Entities.Documents, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, search, projectId, contractorId } = queryParams
      const { data } = await api.v1AdminDocumentsGet(
        limit,
        offset,
        sort,
        search,
        projectId,
        contractorId,
      )
      return data
    },
    options,
  )
}
